import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { TeamRepoScore } from '@sdd-maturity/shared-types';
import { FrameworkBadge } from './FrameworkBadge';
import { relativeTime, classifyRepos, repoStatus } from '../lib/utils';
import { levelLabel, scoreColor, levelBadgeClass } from '../lib/score';
import { buildRepoPath } from '../lib/hierarchy-route';

export interface RepoTableProps {
  repos: TeamRepoScore[];
  /** FR-5: owning team, threaded into the canonical leaf URL for each repo row. */
  teamNumber?: string;
  showComponent?: boolean;
  groupByComponent?: boolean;
  /** Optional content rendered directly under the Active repos table (e.g. the
   *  team scan status line), above the collapsed "Other repos" section. */
  afterActive?: React.ReactNode;
}

type SortColumn = 'name' | 'status' | 'score' | 'level' | 'framework' | 'component' | 'recommendations' | 'lastActivity' | 'lastScanned';
type SortDirection = 'asc' | 'desc';

const levelOrdinal: Record<string, number> = { L1: 1, L2: 2, L3: 3, L4: 4, L5: 5 };

function compareRepos(a: TeamRepoScore, b: TeamRepoScore, column: SortColumn, direction: SortDirection): number {
  let result = 0;
  switch (column) {
    case 'name':
      result = a.repoName.localeCompare(b.repoName);
      break;
    case 'status':
      result = repoStatus(a).ordinal - repoStatus(b).ordinal;
      break;
    case 'score':
      result = a.overallScore - b.overallScore;
      break;
    case 'level':
      result = (levelOrdinal[a.overallLevel] ?? 0) - (levelOrdinal[b.overallLevel] ?? 0);
      break;
    case 'framework':
      result = a.framework.localeCompare(b.framework);
      break;
    case 'component':
      result = (a.componentName ?? '').localeCompare(b.componentName ?? '');
      break;
    case 'recommendations':
      result = (a.recommendationsCount ?? 0) - (b.recommendationsCount ?? 0);
      break;
    case 'lastActivity': {
      const aDate = a.lastActivityDate ? new Date(a.lastActivityDate).getTime() : 0;
      const bDate = b.lastActivityDate ? new Date(b.lastActivityDate).getTime() : 0;
      result = aDate - bDate;
      break;
    }
    case 'lastScanned': {
      const aScan = a.lastScanTimestamp ? new Date(a.lastScanTimestamp).getTime() : 0;
      const bScan = b.lastScanTimestamp ? new Date(b.lastScanTimestamp).getTime() : 0;
      result = aScan - bScan;
      break;
    }
  }
  return direction === 'asc' ? result : -result;
}

const colCount = 9; // +1 for Status column

function renderStatusCell(repo: TeamRepoScore): React.JSX.Element {
  const { label, badgeClass } = repoStatus(repo);
  return <td><span className={`badge ${badgeClass}`}>{label}</span></td>;
}

/** The `v1 legacy` badge, shared by the active and not-scored rows so the two can't drift. */
function renderV1LegacyBadge(repoName: string): React.JSX.Element {
  return (
    <span
      className="badge badge-legacy"
      aria-label="v1 legacy classification — re-scan to update"
      data-testid={`v1-legacy-${repoName}`}
    >
      v1 legacy
    </span>
  );
}

function renderActiveRow(repo: TeamRepoScore, showComponent: boolean, teamNumber?: string, showStatus = true): React.JSX.Element {
  const lastActivity = repo.lastActivityDate;
  const v1Legacy = (repo as { _v1Legacy?: true })._v1Legacy === true;
  return (
    <tr key={`${repo.orgName}/${repo.repoName}`}>
      <td>
        {/* Canonical leaf URL (owning team in the path); plain text without a team. */}
        {teamNumber ? (
          <Link to={buildRepoPath(teamNumber, repo.orgName, repo.repoName)} data-testid={`repo-card-${repo.repoName}`}>
            {repo.repoName}
          </Link>
        ) : (
          <span data-testid={`repo-card-${repo.repoName}`}>{repo.repoName}</span>
        )}
      </td>
      {showStatus && renderStatusCell(repo)}
      <td className="text-right text-base font-bold" style={{ color: scoreColor(repo.overallScore) }}>{Math.round(repo.overallScore)}</td>
      <td>
        <span className={levelBadgeClass(repo.overallLevel)}>{levelLabel(repo.overallLevel)}</span>
        {v1Legacy && renderV1LegacyBadge(repo.repoName)}
      </td>
      <td>
        <FrameworkBadge framework={repo.framework} />
      </td>
      {showComponent && <td>{repo.componentName ?? '—'}</td>}
      <td className="text-right">{repo.recommendationsCount !== undefined ? repo.recommendationsCount : '—'}</td>
      <td>{lastActivity ? relativeTime(lastActivity) : '—'}</td>
      <td className="text-muted">{repo.lastScanTimestamp ? relativeTime(repo.lastScanTimestamp) : '—'}</td>
    </tr>
  );
}

function renderInactiveRow(repo: TeamRepoScore, showComponent: boolean): React.JSX.Element {
  const repoAny = repo as unknown as Record<string, unknown>;
  const daysInactive = repoAny['daysInactive'] as number | undefined;
  const lastActivity = repo.lastActivityDate;

  return (
    <tr key={`${repo.orgName}/${repo.repoName}`} style={{ opacity: 0.6 }}>
      <td>{repo.repoName}</td>
      {renderStatusCell(repo)}
      <td className="text-right">—</td>
      <td><span className="badge badge-warning">{repo.overallLevel === 'SKIPPED' ? 'Skipped' : repo.overallLevel}</span></td>
      <td><FrameworkBadge framework={repo.framework} /></td>
      {showComponent && <td>{repo.componentName ?? '—'}</td>}
      <td className="text-right">—</td>
      <td>
        {daysInactive
          ? `${daysInactive} days inactive`
          : lastActivity
            ? relativeTime(lastActivity)
            : '30+ days inactive'}
      </td>
      <td className="text-muted">{repo.lastScanTimestamp ? relativeTime(repo.lastScanTimestamp) : '—'}</td>
    </tr>
  );
}

function renderEvalFailedRow(repo: TeamRepoScore, showComponent: boolean): React.JSX.Element {
  return (
    <tr key={`${repo.orgName}/${repo.repoName}`} style={{ opacity: 0.6 }}>
      <td>{repo.repoName}</td>
      {renderStatusCell(repo)}
      <td className="text-right">—</td>
      <td><span className="badge badge-l1">Eval Failed</span></td>
      <td><FrameworkBadge framework={repo.framework} /></td>
      {showComponent && <td>{repo.componentName ?? '—'}</td>}
      <td className="text-right">—</td>
      <td>Could not determine score</td>
      <td className="text-muted">{repo.lastScanTimestamp ? relativeTime(repo.lastScanTimestamp) : '—'}</td>
    </tr>
  );
}

function renderNotScoredRow(repo: TeamRepoScore, showComponent: boolean): React.JSX.Element {
  const label = repo.framework === 'infra-only' ? 'Infrastructure Only' : 'Maintenance Only';
  const badgeClass = repo.framework === 'infra-only' ? 'badge-infra' : 'badge-maintenance';
  const v1Legacy = (repo as { _v1Legacy?: true })._v1Legacy === true;
  return (
    <tr key={`${repo.orgName}/${repo.repoName}`} data-testid={`not-scored-${repo.repoName}`} style={{ opacity: 0.7 }}>
      <td>{repo.repoName}</td>
      {renderStatusCell(repo)}
      <td className="text-right">—</td>
      <td>
        <span className={`badge ${badgeClass}`}>{label}</span>
        {v1Legacy && renderV1LegacyBadge(repo.repoName)}
      </td>
      <td><FrameworkBadge framework={repo.framework} /></td>
      {showComponent && <td>{repo.componentName ?? '—'}</td>}
      <td className="text-right">—</td>
      <td>Not scored — {repo.framework === 'infra-only' ? 'infrastructure/config repo' : 'maintenance commits only'}</td>
      <td className="text-muted">{repo.lastScanTimestamp ? relativeTime(repo.lastScanTimestamp) : '—'}</td>
    </tr>
  );
}

function renderInaccessibleRow(repo: TeamRepoScore, showComponent: boolean): React.JSX.Element {
  return (
    <tr key={`${repo.orgName}/${repo.repoName}`} style={{ opacity: 0.6 }}>
      <td>{repo.repoName}</td>
      {renderStatusCell(repo)}
      <td className="text-right">—</td>
      <td>—</td>
      <td>—</td>
      {showComponent && <td>{repo.componentName ?? '—'}</td>}
      <td className="text-right">—</td>
      <td>Private or token lacks access</td>
      <td className="text-muted">{repo.lastScanTimestamp ? relativeTime(repo.lastScanTimestamp) : '—'}</td>
    </tr>
  );
}

interface SortableHeaderProps {
  label: string;
  column: SortColumn;
  activeColumn: SortColumn;
  direction: SortDirection;
  onSort: (col: SortColumn) => void;
  align?: 'left' | 'right' | 'center';
}

function SortableHeader({ label, column, activeColumn, direction, onSort, align = 'left' }: SortableHeaderProps): React.JSX.Element {
  const isActive = column === activeColumn;
  const ariaSortValue = isActive ? (direction === 'asc' ? 'ascending' : 'descending') : undefined;
  const alignClass = align === 'right' ? ' text-right' : align === 'center' ? ' text-center' : '';
  return (
    <th className={`sortable${alignClass}`} onClick={() => onSort(column)} data-testid={`sort-${column}`} aria-sort={ariaSortValue} style={{ whiteSpace: 'nowrap', color: isActive ? 'var(--genai-sem-text-primary)' : 'var(--genai-sem-text-secondary)' }}>
      {label}{isActive && <span className="sort-indicator" data-testid="sort-indicator">{direction === 'asc' ? ' ▲' : ' ▼'}</span>}
    </th>
  );
}

interface CollapsibleSectionProps {
  testId: string;
  label: string;
  count: number;
  cols: number;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

function CollapsibleSection({ testId, label, count, cols, defaultExpanded = true, children }: CollapsibleSectionProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <tbody data-testid={testId}>
      <tr>
        <th
          colSpan={cols}
          className="section-header-cell"
          onClick={() => setExpanded((prev) => !prev)}
          style={{ cursor: 'pointer', userSelect: 'none' }}
          aria-expanded={expanded}
        >
          <span className="section-header">
            <span className="section-toggle">{expanded ? '▾' : '▸'}</span>
            {label} ({count})
          </span>
        </th>
      </tr>
      {expanded && children}
    </tbody>
  );
}

export function RepoTable({ repos, teamNumber, showComponent = false, groupByComponent = false, afterActive }: RepoTableProps): React.JSX.Element {
  const [sortColumn, setSortColumn] = useState<SortColumn>('lastActivity');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [otherExpanded, setOtherExpanded] = useState(false);

  const handleSort = (column: SortColumn): void => {
    if (column === sortColumn) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection(column === 'name' ? 'asc' : 'desc');
    }
  };

  const { active, inactive, inaccessible, evalFailed, notScored } = useMemo(() => classifyRepos(repos), [repos]);

  const sortedActive = useMemo(
    () => [...active].sort((a, b) => compareRepos(a, b, sortColumn, sortDirection)),
    [active, sortColumn, sortDirection],
  );

  // All non-active repos collapse into one "Other repos" section. They stay grouped
  // by status (ordinal asc) so like sits with like, then fall back to the active
  // sort column — the per-row Status badge keeps the flat list self-describing.
  const sortedOther = useMemo(() => {
    return [...inactive, ...inaccessible, ...evalFailed, ...notScored].sort((a, b) => {
      const ordinalDiff = repoStatus(a).ordinal - repoStatus(b).ordinal;
      if (ordinalDiff !== 0) return ordinalDiff;
      return compareRepos(a, b, sortColumn, sortDirection);
    });
  }, [inactive, inaccessible, evalFailed, notScored, sortColumn, sortDirection]);

  const componentGroups = useMemo(() => {
    if (!groupByComponent) return null;
    const groups = new Map<string, TeamRepoScore[]>();
    for (const repo of repos) {
      const key = repo.componentName || 'Unassigned';
      const list = groups.get(key) ?? [];
      list.push(repo);
      groups.set(key, list);
    }
    const sorted = [...groups.entries()].sort(([a], [b]) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
    return sorted.map(([name, groupRepos]) => ({
      name,
      repos: [...groupRepos].sort((a, b) => compareRepos(a, b, sortColumn, sortDirection)),
    }));
  }, [groupByComponent, repos, sortColumn, sortDirection]);

  /** Routes a repo to its correct row renderer — used by component-group view where
   *  repos from different status buckets coexist in the same group. */
  function renderAnyRow(repo: TeamRepoScore): React.JSX.Element {
    switch (repoStatus(repo).status) {
      case 'active':
        return renderActiveRow(repo, showComponent, teamNumber);
      case 'inactive':
        return renderInactiveRow(repo, showComponent);
      case 'evalFailed':
        return renderEvalFailedRow(repo, showComponent);
      case 'inaccessible':
        return renderInaccessibleRow(repo, showComponent);
      case 'notScored':
        return renderNotScoredRow(repo, showComponent); // Maintenance Only / Infra Only
    }
  }

  const cols = showComponent ? colCount : colCount - 1;
  // Active table drops the Status column — every row is "Active".
  const activeCols = cols - 1;

  const makeHeaderRow = (showStatus: boolean): React.JSX.Element => (
    <tr>
      <SortableHeader label="Repo Name" column="name" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
      {showStatus && <SortableHeader label="Status" column="status" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />}
      <SortableHeader label="Score" column="score" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} align="right" />
      <SortableHeader label="Level" column="level" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
      <SortableHeader label="Framework" column="framework" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
      {showComponent && <SortableHeader label="Component" column="component" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />}
      <SortableHeader label="Recommendations" column="recommendations" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} align="right" />
      <SortableHeader label="Last Activity" column="lastActivity" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
      <SortableHeader label="Last Scanned" column="lastScanned" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
    </tr>
  );
  const headerRow = makeHeaderRow(true);
  const activeHeaderRow = makeHeaderRow(false);

  if (groupByComponent && componentGroups) {
    return (
      <div data-testid="repo-list">
        <div className="card card-flush" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="repo-table">
            <thead>{headerRow}</thead>
            {componentGroups.map(({ name, repos: groupRepos }) => (
              <CollapsibleSection key={name} testId={`component-group-${name}`} label={name} count={groupRepos.length} cols={cols}>
                {groupRepos.map((repo) => renderAnyRow(repo))}
              </CollapsibleSection>
            ))}
          </table>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="repo-list">
      {/* Active repos — the set the rollup above describes. Its own table/section. */}
      <section data-testid="section-active" style={{ marginBottom: 'var(--genai-space-lg)' }}>
        <h3 className="text-base font-semibold mb-sm">Active repos ({sortedActive.length})</h3>
        <div className="card card-flush" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="repo-table">
            <thead>{activeHeaderRow}</thead>
            <tbody>
              {sortedActive.length > 0 ? (
                sortedActive.map((repo) => renderActiveRow(repo, showComponent, teamNumber, false))
              ) : (
                <tr>
                  <td colSpan={activeCols} className="text-muted" style={{ padding: 'var(--genai-space-md)', textAlign: 'center' }}>
                    No active repos
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {afterActive && <div className="mt-sm">{afterActive}</div>}
      </section>

      {/* Other repos — inactive / not accessible / eval failed / not scored, in a
          separate collapsed section with its own table. The Status column tells them apart. */}
      {sortedOther.length > 0 && (
        <section data-testid="section-other">
          <button
            type="button"
            onClick={() => setOtherExpanded((prev) => !prev)}
            aria-expanded={otherExpanded}
            className="section-header"
            style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit', display: 'flex', alignItems: 'center', gap: '6px', padding: 'var(--genai-space-xs) 0' }}
          >
            <span className="section-toggle">{otherExpanded ? '▾' : '▸'}</span>
            <span className="text-base font-semibold">Other repos ({sortedOther.length})</span>
          </button>
          {otherExpanded && (
            <div className="card card-flush" style={{ padding: 0, overflowX: 'auto', marginTop: 'var(--genai-space-sm)' }}>
              <table className="repo-table">
                <thead>{headerRow}</thead>
                <tbody>{sortedOther.map((repo) => renderAnyRow(repo))}</tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
